// Admin query layer. Pairs with `lib/views/admin.ts` + `lib/views/admin-resources.ts`
// (server fetchers used during SSR prefetch) and `lib/mutations/admin.ts`.
// No 'use client' directive: hooks are React hooks called from client
// components; server pages import the query keys to prefetch.

import {
  useQuery,
  useInfiniteQuery,
  keepPreviousData,
  type UseQueryOptions,
} from '@tanstack/react-query';
import type { ResourceKind } from '@leanin/shared/resource-kinds';
import { getApiClient } from '../api-client';
import { throwApiError } from '../api-error';

// ---- Re-exported parameter types (mirror views helpers) ----

export type AnalyticsWindowDays = 30 | 90 | 180 | 365;
export const DEFAULT_ANALYTICS_WINDOW_DAYS: AnalyticsWindowDays = 90;

export type MemberAdminStatus =
  | 'active'
  | 'pending'
  | 'restricted'
  | 'deactivated'
  | 'banned';

export type AdminPlatformRoleFilter = 'admin' | 'moderator' | 'none';
export type AdminCommunityRoleFilter = 'circle_leader' | 'network_leader';
export interface ListAdminMembersInput {
  readonly q?: string;
  readonly status?: MemberAdminStatus;
  readonly platformRole?: AdminPlatformRoleFilter;
  readonly communityRole?: AdminCommunityRoleFilter;
  readonly cursor?: string;
  readonly pageSize?: number;
}

export interface InvitationsPageParams {
  readonly status?: 'pending' | 'accepted' | 'revoked' | 'expired';
  readonly q?: string;
  readonly cursor?: string;
  readonly pageSize?: number;
}

export interface AuditPageParams {
  readonly action?: string;
  readonly actor?: string;
  readonly outcome?: 'success' | 'failure';
  readonly from?: string;
  readonly to?: string;
  readonly cursor?: string;
  readonly pageSize?: number;
}

export interface ConsentPageParams {
  readonly userId?: string;
  readonly purpose?:
    | 'marketing'
    | 'analytics'
    | 'email_digest'
    | 'ai_processing'
    | 'directory_visible'
    | 'ranking_signal'
    | 'cookie_strict'
    | 'cookie_analytics'
    | 'cookie_marketing';
  readonly granted?: boolean;
  readonly from?: string;
  readonly to?: string;
  readonly cursor?: string;
  readonly pageSize?: number;
}

export interface DsrPageParams {
  readonly status?:
    | 'received'
    | 'approved'
    | 'running'
    | 'completed'
    | 'rejected'
    | 'overdue'
    | 'cancelled';
  readonly kind?: string;
  readonly cursor?: string;
  readonly pageSize?: number;
}

export interface SensitiveReadPageParams {
  readonly actor?: string;
  readonly resourceKind?:
    | 'member_profile'
    | 'message_thread'
    | 'dsr_export_bundle'
    | 'audit_log_query'
    | 'consent_history'
    | 'bulk_export';
  readonly resourceId?: string;
  readonly from?: string;
  readonly to?: string;
  readonly cursor?: string;
  readonly pageSize?: number;
}

export interface BroadcastsPageParams {
  readonly status?: 'draft' | 'scheduled' | 'sent';
  readonly cursor?: string;
  readonly pageSize?: number;
  readonly q?: string;
}

export interface AdminReportQueueInput {
  readonly cursor?: string;
  readonly pageSize?: number;
  readonly status?: 'open' | 'triaged' | 'actioned' | 'dismissed';
  readonly targetKind?:
    | 'post'
    | 'comment'
    | 'message'
    | 'event'
    | 'resource'
    | 'member';
  readonly reason?:
    | 'spam'
    | 'harassment'
    | 'self_harm'
    | 'illegal'
    | 'other';
}

export type AdminResourceStatus = 'all' | 'draft' | 'published' | 'featured';
export type AdminResourceContentLayer =
  | 'community'
  | 'network'
  | 'public';

export interface ListAdminResourcesInput {
  readonly status?: AdminResourceStatus;
  readonly contentLayer?: AdminResourceContentLayer;
  readonly kind?: ResourceKind;
  readonly q?: string;
  readonly cursor?: string;
  readonly pageSize?: number;
}

export type EngagementScope = 'circles' | 'networks';
export type EngagementPeriod = '30d' | '90d' | 'lifetime';
export type EngagementSortBy = 'activityScore' | 'memberCount' | 'lastActivity';
export type EngagementSortDir = 'asc' | 'desc';

export interface EngagementListParams {
  readonly scope: EngagementScope;
  readonly period: EngagementPeriod;
  readonly sortBy: EngagementSortBy;
  readonly sortDir: EngagementSortDir;
  readonly pageSize?: number;
}

export interface EngagementDetailParams {
  readonly scope: 'circle' | 'network';
  readonly id: string;
  readonly period: EngagementPeriod;
}

export type AudienceSpec =
  | { readonly kind: 'all' }
  | { readonly kind: 'circles'; readonly circleIds: readonly string[] }
  | { readonly kind: 'networks'; readonly networkIds: readonly string[] }
  | { readonly kind: 'members'; readonly userIds: readonly string[] }
  | { readonly kind: 'role'; readonly role: 'leader' | 'admin' }
  | { readonly kind: 'recent_active'; readonly days: number };

export interface PickerInput {
  readonly q?: string;
  readonly cursor?: string;
  readonly pageSize?: number;
}

// ---- Query keys ----

export const adminKeys = {
  all: ['admin'] as const,
  analytics: (windowDays: AnalyticsWindowDays) =>
    [...adminKeys.all, 'analytics', windowDays] as const,
  members: () => [...adminKeys.all, 'members'] as const,
  membersList: (params: ListAdminMembersInput) =>
    [...adminKeys.members(), 'list', params] as const,
  memberDetail: (id: string) =>
    [...adminKeys.members(), 'detail', id] as const,
  memberStatusCounts: () => [...adminKeys.members(), 'status-counts'] as const,
  memberSearch: (params: PickerInput) =>
    [...adminKeys.members(), 'search', params] as const,
  invitations: (params: InvitationsPageParams) =>
    [...adminKeys.all, 'invitations', params] as const,
  audit: (params: AuditPageParams) =>
    [...adminKeys.all, 'audit', params] as const,
  consent: (params: ConsentPageParams) =>
    [...adminKeys.all, 'consent', params] as const,
  dsr: (params: DsrPageParams) =>
    [...adminKeys.all, 'dsr', 'list', params] as const,
  dsrDetail: (id: string) => [...adminKeys.all, 'dsr', 'detail', id] as const,
  sensitiveRead: (params: SensitiveReadPageParams) =>
    [...adminKeys.all, 'sensitive-read', params] as const,
  broadcasts: () => [...adminKeys.all, 'broadcasts'] as const,
  broadcastsList: (params: BroadcastsPageParams) =>
    [...adminKeys.broadcasts(), 'list', params] as const,
  broadcastStatusCounts: () =>
    [...adminKeys.broadcasts(), 'status-counts'] as const,
  broadcastAudience: (spec: AudienceSpec) =>
    [...adminKeys.broadcasts(), 'audience', spec] as const,
  moderation: () => [...adminKeys.all, 'moderation'] as const,
  moderationQueue: (params: AdminReportQueueInput) =>
    [...adminKeys.moderation(), 'queue', params] as const,
  moderationReport: (id: string) =>
    [...adminKeys.moderation(), 'report', id] as const,
  moderationStats: () => [...adminKeys.moderation(), 'stats'] as const,
  moderationStatusCounts: () =>
    [...adminKeys.moderation(), 'status-counts'] as const,
  resources: () => [...adminKeys.all, 'resources'] as const,
  resourcesList: (params: ListAdminResourcesInput) =>
    [...adminKeys.resources(), 'list', params] as const,
  resourceDetail: (id: string) =>
    [...adminKeys.resources(), 'detail', id] as const,
  engagementList: (params: EngagementListParams) =>
    [...adminKeys.all, 'engagement', 'list', params] as const,
  engagementDetail: (params: EngagementDetailParams) =>
    [...adminKeys.all, 'engagement', 'detail', params] as const,
  workers: () => [...adminKeys.all, 'workers'] as const,
  sqsDepths: () => [...adminKeys.all, 'sqs-depths'] as const,
  outboxLag: () => [...adminKeys.all, 'outbox-lag'] as const,
  partitionCoverage: () => [...adminKeys.all, 'partition-coverage'] as const,
  health: () => [...adminKeys.all, 'health'] as const,
  circlesPicker: (params: PickerInput) =>
    [...adminKeys.all, 'circles-picker', params] as const,
  networksPicker: (params: PickerInput) =>
    [...adminKeys.all, 'networks-picker', params] as const,
};

// ---- Client fetchers ----

export async function fetchAdminAnalytics(
  windowDays: AnalyticsWindowDays = DEFAULT_ANALYTICS_WINDOW_DAYS,
) {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/api/v1/admin/analytics/overview',
    {
      params: {
        query: { days: String(windowDays) as '30' | '90' | '180' | '365' },
      },
    },
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminMembersPage(params: ListAdminMembersInput) {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/admin/members', {
    params: { query: params },
  });
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminMemberDetail(userId: string) {
  const client = getApiClient();
  const { data, error, response } = await client.GET('/api/v1/admin/members/{id}', {
    params: { path: { id: userId } },
  });
  if (data === undefined) throwApiError(error, response);
  return data.data;
}

export async function fetchAdminMemberStatusCounts() {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/api/v1/admin/members/status-counts',
    {},
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminInvitations(params: InvitationsPageParams) {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/admin/invitations', {
    params: { query: params },
  });
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminAudit(params: AuditPageParams) {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/admin/audit-log', {
    params: { query: params },
  });
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminConsent(params: ConsentPageParams) {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/api/v1/admin/consent-events',
    { params: { query: params } },
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminDsr(params: DsrPageParams) {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/api/v1/admin/dsr/requests',
    {
      params: {
        query: {
          cursor: params.cursor,
          pageSize: params.pageSize,
          status: params.status,
          kind: params.kind as
            | 'access'
            | 'rectification'
            | 'erasure'
            | 'restriction'
            | 'portability'
            | 'objection'
            | undefined,
        },
      },
    },
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminDsrDetail(id: string) {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/api/v1/admin/dsr/requests/{id}',
    { params: { path: { id } } },
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminSensitiveRead(
  params: SensitiveReadPageParams,
) {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/api/v1/admin/sensitive-read',
    { params: { query: params } },
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminBroadcasts(params: BroadcastsPageParams) {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/admin/broadcasts', {
    params: {
      query: {
        cursor: params.cursor,
        pageSize: params.pageSize,
        status: params.status,
      },
    },
  });
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminBroadcastStatusCounts() {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/api/v1/admin/broadcasts/status-counts',
    {},
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminBroadcastAudience(spec: AudienceSpec) {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/api/v1/admin/broadcasts/audience-resolve',
    { params: { query: { spec: JSON.stringify(spec) } } },
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminModerationQueue(
  params: AdminReportQueueInput,
) {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/api/v1/admin/moderation/reports',
    { params: { query: params } },
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminModerationReport(reportId: string) {
  const client = getApiClient();
  const { data, error, response } = await client.GET(
    '/api/v1/admin/moderation/reports/{id}',
    { params: { path: { id: reportId } } },
  );
  if (data === undefined) throwApiError(error, response);
  return data;
}

export async function fetchAdminModerationStats() {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/api/v1/admin/moderation/stats',
    {},
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminModerationStatusCounts() {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/api/v1/admin/moderation/status-counts',
    {},
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminResourcesPage(
  params: ListAdminResourcesInput,
) {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/admin/resources', {
    params: { query: params },
  });
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminResourceDetail(resourceId: string) {
  const client = getApiClient();
  const { data, error, response } = await client.GET(
    '/api/v1/admin/resources/{id}',
    { params: { path: { id: resourceId } } },
  );
  if (data === undefined) throwApiError(error, response);
  return data.data;
}

export async function fetchAdminEngagementList(params: EngagementListParams) {
  const client = getApiClient();
  if (params.scope === 'circles') {
    const { data, error } = await client.GET(
      '/api/v1/admin/circles/engagement',
      {
        params: {
          query: {
            period: params.period,
            sortBy: params.sortBy,
            sortDir: params.sortDir,
            pageSize: params.pageSize,
          },
        },
      },
    );
    if (data === undefined) throwApiError(error);
    return data;
  }
  const { data, error } = await client.GET(
    '/api/v1/admin/networks/engagement',
    {
      params: {
        query: {
          period: params.period,
          sortBy: params.sortBy,
          sortDir: params.sortDir,
          pageSize: params.pageSize,
        },
      },
    },
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminEngagementDetail(
  params: EngagementDetailParams,
) {
  const client = getApiClient();
  if (params.scope === 'circle') {
    const { data, error } = await client.GET(
      '/api/v1/admin/circles/{id}/engagement',
      {
        params: {
          path: { id: params.id },
          query: { period: params.period },
        },
      },
    );
    if (data === undefined) throwApiError(error);
    return data;
  }
  const { data, error } = await client.GET(
    '/api/v1/admin/networks/{id}/engagement',
    {
      params: {
        path: { id: params.id },
        query: { period: params.period },
      },
    },
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminWorkers() {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/admin/workers', {});
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminSqsDepths() {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/admin/sqs-depths', {});
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminOutboxLag() {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/admin/outbox-lag', {});
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminPartitionCoverage() {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/api/v1/admin/partition-coverage',
    {},
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminHealth() {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/admin/health', {});
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminCirclesForPicker(params: PickerInput = {}) {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/admin/circles', {
    params: { query: params },
  });
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminNetworksForPicker(params: PickerInput = {}) {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/admin/networks', {
    params: { query: params },
  });
  if (data === undefined) throwApiError(error);
  return data;
}

export async function searchAdminMembersForPicker(params: PickerInput = {}) {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/api/v1/admin/members/search',
    { params: { query: params } },
  );
  if (data === undefined) throwApiError(error);
  return data;
}

// ---- Hooks ----

export function useAdminAnalytics(
  windowDays: AnalyticsWindowDays = DEFAULT_ANALYTICS_WINDOW_DAYS,
  options?: Omit<
    UseQueryOptions<
      Awaited<ReturnType<typeof fetchAdminAnalytics>>,
      Error,
      Awaited<ReturnType<typeof fetchAdminAnalytics>>
    >,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery({
    queryKey: adminKeys.analytics(windowDays),
    queryFn: () => fetchAdminAnalytics(windowDays),
    ...options,
  });
}

export function useAdminMembersPage(params: ListAdminMembersInput) {
  return useQuery({
    queryKey: adminKeys.membersList(params),
    queryFn: () => fetchAdminMembersPage(params),
    placeholderData: keepPreviousData,
  });
}

export function useInfiniteAdminMembers(
  input: Omit<ListAdminMembersInput, 'cursor'>,
) {
  return useInfiniteQuery({
    queryKey: adminKeys.membersList(input),
    queryFn: ({ pageParam }) =>
      fetchAdminMembersPage({ ...input, cursor: pageParam ?? undefined }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      (lastPage as { nextCursor?: string | null }).nextCursor ?? undefined,
  });
}

export function useAdminMemberDetail(userId: string) {
  return useQuery({
    queryKey: adminKeys.memberDetail(userId),
    queryFn: () => fetchAdminMemberDetail(userId),
    enabled: userId.length > 0,
  });
}

export function useAdminMemberStatusCounts() {
  return useQuery({
    queryKey: adminKeys.memberStatusCounts(),
    queryFn: fetchAdminMemberStatusCounts,
  });
}

export function useAdminInvitations(params: InvitationsPageParams) {
  return useQuery({
    queryKey: adminKeys.invitations(params),
    queryFn: () => fetchAdminInvitations(params),
  });
}

export function useAdminAudit(params: AuditPageParams) {
  return useQuery({
    queryKey: adminKeys.audit(params),
    queryFn: () => fetchAdminAudit(params),
  });
}

export function useAdminConsent(params: ConsentPageParams) {
  return useQuery({
    queryKey: adminKeys.consent(params),
    queryFn: () => fetchAdminConsent(params),
  });
}

export function useAdminDsr(params: DsrPageParams) {
  return useQuery({
    queryKey: adminKeys.dsr(params),
    queryFn: () => fetchAdminDsr(params),
  });
}

export function useAdminDsrDetail(id: string) {
  return useQuery({
    queryKey: adminKeys.dsrDetail(id),
    queryFn: () => fetchAdminDsrDetail(id),
    enabled: id.length > 0,
  });
}

export function useAdminSensitiveRead(params: SensitiveReadPageParams) {
  return useQuery({
    queryKey: adminKeys.sensitiveRead(params),
    queryFn: () => fetchAdminSensitiveRead(params),
  });
}

export function useAdminBroadcasts(params: BroadcastsPageParams) {
  return useQuery({
    queryKey: adminKeys.broadcastsList(params),
    queryFn: () => fetchAdminBroadcasts(params),
  });
}

export function useAdminBroadcastStatusCounts() {
  return useQuery({
    queryKey: adminKeys.broadcastStatusCounts(),
    queryFn: fetchAdminBroadcastStatusCounts,
  });
}

export function useAdminBroadcastAudience(spec: AudienceSpec, enabled = true) {
  return useQuery({
    queryKey: adminKeys.broadcastAudience(spec),
    queryFn: () => fetchAdminBroadcastAudience(spec),
    enabled,
  });
}

export function useAdminModerationQueue(params: AdminReportQueueInput) {
  return useQuery({
    queryKey: adminKeys.moderationQueue(params),
    queryFn: () => fetchAdminModerationQueue(params),
  });
}

export function useAdminModerationReport(reportId: string) {
  return useQuery({
    queryKey: adminKeys.moderationReport(reportId),
    queryFn: () => fetchAdminModerationReport(reportId),
    enabled: reportId.length > 0,
  });
}

export function useAdminModerationStats() {
  return useQuery({
    queryKey: adminKeys.moderationStats(),
    queryFn: fetchAdminModerationStats,
  });
}

export function useAdminModerationStatusCounts() {
  return useQuery({
    queryKey: adminKeys.moderationStatusCounts(),
    queryFn: fetchAdminModerationStatusCounts,
  });
}

export function useAdminResourcesPage(params: ListAdminResourcesInput) {
  return useQuery({
    queryKey: adminKeys.resourcesList(params),
    queryFn: () => fetchAdminResourcesPage(params),
  });
}

export function useAdminResourceDetail(resourceId: string) {
  return useQuery({
    queryKey: adminKeys.resourceDetail(resourceId),
    queryFn: () => fetchAdminResourceDetail(resourceId),
    enabled: resourceId.length > 0,
  });
}

export function useAdminEngagementList(params: EngagementListParams) {
  return useQuery({
    queryKey: adminKeys.engagementList(params),
    queryFn: () => fetchAdminEngagementList(params),
  });
}

export function useAdminEngagementDetail(params: EngagementDetailParams) {
  return useQuery({
    queryKey: adminKeys.engagementDetail(params),
    queryFn: () => fetchAdminEngagementDetail(params),
  });
}

export function useAdminWorkers() {
  return useQuery({
    queryKey: adminKeys.workers(),
    queryFn: fetchAdminWorkers,
  });
}

export function useAdminHealth() {
  return useQuery({
    queryKey: adminKeys.health(),
    queryFn: fetchAdminHealth,
  });
}

export function useAdminCirclesForPicker(params: PickerInput = {}) {
  return useQuery({
    queryKey: adminKeys.circlesPicker(params),
    queryFn: () => fetchAdminCirclesForPicker(params),
  });
}

export function useAdminNetworksForPicker(params: PickerInput = {}) {
  return useQuery({
    queryKey: adminKeys.networksPicker(params),
    queryFn: () => fetchAdminNetworksForPicker(params),
  });
}
